'use client';

import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn, hasValue } from '@woonstad/helpers-utils';

import { Typography } from '../../typography/Typography';
import { useFormField } from '../FormField';

type Props<T = string> = {
  labelTrue?: string;
  labelFalse?: string;
  sublabel?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onCheckedChange?: SwitchPrimitive.SwitchProps['onCheckedChange'];
  transformValueOnChange?: (value: SwitchPrimitive.SwitchProps['checked']) => T;
  checked?: boolean;
  disabled?: boolean;
  id?: string;
  testid?: string;
};

export function Switch<T = string>({
  labelTrue,
  labelFalse,
  sublabel,
  onClick,
  disabled,
  onCheckedChange,
  checked,
  id,
  transformValueOnChange,
  testid,
}: Props<T>) {
  const {
    field,
    formItemId,
  } = useFormField();

  const inputClasses = cn(
    'flex h-7 w-14 shrink-0 items-center justify-center rounded-full border-2 outline-offset-4',
    {
      'border-checkbox-enabled hover:border-checkbox-hover': !field?.disabled,
      'border-checkbox-disabled': field?.disabled,
    },
  );

  const isChecked = hasValue(checked) ? checked : field?.value;
  const convertedValue = isChecked ? 'on' : 'off';
  const isDisabled = disabled || field?.disabled;
  const fieldId = id ?? formItemId;

  return (
    <div
      className={cn('group flex w-fit text-checkbox-enabled hover:text-checkbox-hover')}
      data-testid={testid}
    >
      <label
        className={cn('flex items-center', {
          'cursor-pointer': !isDisabled,
        })}
        htmlFor={fieldId}
      >
        { hasValue(labelFalse) && (
          <Typography
            data-disabled={isDisabled}
            type="span"
            variant="content-body"
            className="mr-2"
          >
            <span
              className={cn('block',
                !isDisabled && 'text-checkbox-enabled group-hover:text-checkbox-hover',
                isDisabled && 'text-checkbox-disabled')}
            >
              {labelFalse}
            </span>
          </Typography>
        )}

        <SwitchPrimitive.Root
          onClick={onClick}
          aria-label={labelTrue}
          checked={isChecked}
          name={field?.name}
          className={inputClasses}
          disabled={isDisabled}
          id={fieldId}
          onBlur={field?.onBlur}
          value={convertedValue}
          onCheckedChange={(state) => {
            const transformedValue = hasValue(transformValueOnChange) ? transformValueOnChange(state) : state;

            if (onCheckedChange) {
              onCheckedChange(state);
            }

            field?.onChange(transformedValue);
          }}
        >
          <SwitchPrimitive.Thumb
            className={cn(
              'size-5 rounded-full transition-transform',
              !isDisabled && 'bg-checkbox-enabled-toggle',
              isChecked && 'translate-x-[.875rem]',
              !isChecked && 'translate-x-[-.875rem] bg-surface-dimmer',
            )}
          />
        </SwitchPrimitive.Root>

        { hasValue(labelTrue) && (
          <Typography
            data-disabled={isDisabled}
            type="span"
            variant="content-body"
            className="ml-2"
          >
            <span
              className={cn('block',
                !isDisabled && 'text-checkbox-enabled group-hover:text-checkbox-hover',
                isDisabled && 'text-checkbox-disabled')}
            >
              {labelTrue}
            </span>
          </Typography>
        )}
      </label>

      {sublabel && (
        <Typography
          data-disabled={isDisabled}
          textColorClassname="text-input-hint data-[disabled]:text-checkbox-disabled"
          type="span"
          variant="label-small"
        >
          <span className={cn('block', !isDisabled && 'cursor-pointer')}>
            {sublabel}
          </span>
        </Typography>
      )}
    </div>
  );
}
